import { useMemo } from "react";

import { Options, TooltipFormatterContextObject } from "highcharts";
import { IntlShape } from "react-intl";

import formatSeconds from "@app/scene/dashboard/utils/formatSeconds";
import colors from "@app/styles/_colors.module.scss";

import messages from "../messages";

import styles from "./useChartConfig.module.scss";

const normalColor = [colors.primary025, colors.softGray];
const hoverColor = [colors.primary050, colors.softSlate];

const useChartConfig = (
  verifiedCount: number,
  unverifiedCount: number | undefined,
  intl: IntlShape
): Options => {
  const total = verifiedCount + (unverifiedCount ?? 0);

  return useMemo(() => {
    return {
      chart: {
        height: 64,
        marginBottom: 0,
        marginLeft: 0,
        marginRight: 0,
        marginTop: 0,
        plotShadow: false,
        spacingBottom: 0,
        spacingLeft: 0,
        spacingRight: 0,
        spacingTop: 0,
        width: 64,
      },
      credits: undefined,
      legend: {
        enabled: false,
      },
      plotOptions: {
        pie: {
          colors: normalColor,
          dataLabels: {
            enabled: false,
          },
          point: {
            events: {
              mouseOut() {
                this.update({ color: normalColor[this.index] });
              },
              mouseOver() {
                this.update({ color: hoverColor[this.index] });
              },
            },
          },
          states: {
            hover: {
              halo: {
                opacity: 0.25,
                size: 5,
              },
            },
          },
        },
      },
      series: [
        {
          data: [
            {
              name: intl.formatMessage(messages.faceTimeVerified),
              y: verifiedCount,
            },
            {
              name: intl.formatMessage(messages.faceTimeUnverified),
              y: unverifiedCount,
            },
          ],
          startAngle: 0,
          type: "pie",
        },
      ],
      title: {
        text: "",
      },
      tooltip: {
        backgroundColor: colors.slate,
        borderRadius: 4,
        borderWidth: 0,
        className: styles.tooltip,
        followPointer: false,
        formatter: function (this: TooltipFormatterContextObject): string {
          return `${this.key}<br/><b>${formatSeconds(intl, this.y)}${
            total !== 0
              ? ` • ${intl.formatNumber(this.y / total, {
                  maximumFractionDigits: 1,
                  style: "percent",
                })}`
              : ""
          }</b>`;
        },
        positioner: function (labelWidth, labelHeight) {
          const chart = this.chart;
          return {
            x: chart.plotLeft + chart.chartWidth,
            y: chart.plotTop + (chart.chartHeight - labelHeight) / 2,
          };
        },
        useHTML: true,
      },
    };
  }, [intl, verifiedCount, unverifiedCount, total]);
};

export default useChartConfig;
